import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { AuthLayout } from '../../layouts/AuthLayout';
import { TextField, Button, Box, Typography, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth.js';
import toast from 'react-hot-toast';

export const Login = () => {
  const navigate = useNavigate();
  const { login, googleSignIn, isAuthenticated, loading, error, clearError, user } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    try {
      const loggedInUser = await login(formData.email, formData.password);
      toast.success('Login successful');

      // Redirect based on user role
      if (loggedInUser?.role === 'superadmin') {
        navigate('/super-admin/dashboard');
      } else if (loggedInUser?.role === 'vendor') {
        navigate('/vendor/dashboard');
      } else {
        navigate('/dashboard'); // fallback
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Login failed';
      toast.error(msg);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (credentialResponse?.credential) {
      try {
        const loggedInUser = await googleSignIn(credentialResponse.credential);
        toast.success('Google sign-in successful');

        // Redirect based on user role
        if (loggedInUser?.role === 'superadmin') {
          navigate('/super-admin/dashboard');
        } else if (loggedInUser?.role === 'vendor') {
          navigate('/vendor/dashboard');
        } else {
          navigate('/dashboard'); // fallback
        }
      } catch (err) {
        toast.error(err.response?.data?.message || err.message || 'Google sign-in failed');
      }
    }
  };

  const handleGoogleError = () => {
    toast.error('Google authentication failed');
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      // Redirect based on user role for already authenticated users
      if (user.role === 'superadmin') {
        navigate('/super-admin/dashboard');
      } else if (user.role === 'vendor') {
        navigate('/vendor/dashboard');
      } else {
        navigate('/dashboard'); // fallback
      }
    }
  }, [isAuthenticated, user, navigate]);

  return (
    <AuthLayout>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#006494',
              },
            },
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#006494',
              },
            },
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2, borderRadius: 1 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2.5, py: 1.25, textTransform: 'none', fontSize: '1rem', fontWeight: 600 }}
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>

        <Box sx={{ textAlign: 'center', mb: 2.5, py: 1.5, borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary">
            or continue with
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={false}
            theme="outline"
            size="large"
            text="signin_with"
            shape="rectangular"
            ux_mode="popup"
          />
        </Box>
      </Box>
    </AuthLayout>
  );
};
